-- | Top-level app runtime entrypoint.
module App.Runtime
    ( stepWidget
    , stepWidgetFromState
    ) where

import Common
import App.Browser (listenBrowserNavigation, pushRoute, readLocation, replaceRoute)
import App.Hydrate (deriveAppState, loadAppStateWidget)
import App.Msg (Msg(..))
import App.Operation.Runner as OperationRunner
import App.Operation.Store as OpStore
import App.Operation.Types (OperationScope(..))
import App.Route as AppRoute
import App.State (AppState, RouteHistoryMode(..), Step1Session(..), Step2ASession(..), Step2BSession(..), Step3Session(..))
import App.Step3DocumentRequest as Step3DocumentRequest
import App.Update (Command(..), UpdateResult, bootstrap, update)
import Concur.React.DOM as D
import Control.Alt ((<|>))
import Data.Argonaut as JSON
import Data.Array as Arr
import Data.Time.Duration (Milliseconds(..))
import Effect.Aff (delay, effectCanceler, launchAff_, makeAff)
import Effect.Console as Console
import Effect.Ref (Ref)
import Effect.Ref as Ref
import Error (handleCritical, reportError, wrapCritical)
import Form.StudentSelect as StudentSelect
import Internal.JSUtil (allowPageUnload, blockPageUnload, manualSavePrompt)
import Routing.PushState (LocationState, PushStateInterface)
import Steps.FormPicker as FormPicker
import Steps.FormPreview (previewWidget)
import App.Step (Step(..))
import Steps.Step3.Scroll (scrollSectionIntoView)
import Steps.Step3.State as Step3State
import Steps.Step3.Transition (Step3HistoryMode(..))
import Steps.Step3.Update as Step3Update
import Steps.Step3.View (renderStep3)
import Steps.WorkQueue.DocumentIndex (listDocs)
import Steps.WorkQueue.View (loadingWidgetWQ)
import Steps.Step1.State (finishLoading, startLoading) as Step1
import Steps.Step1.View (step1MessageWidget) as Step1
import Web.HTML as JS
import Web.HTML.Window as Window
import Widgets.Form.TestForm (renderTestForm)
import Widgets.Loading (loadingWidget)

type Mailbox = Ref
    { queue :: Array Msg
    , waiter :: Maybe
        { loopId :: Int
        , wake :: Msg -> Effect Unit
        }
    }

type LoopEpoch = Ref Int

step1SearchDebounceDelay :: Milliseconds
step1SearchDebounceDelay = Milliseconds 300.0

stepWidget :: Widget HTML Unit
stepWidget = do
    initial <- wrapCritical =<< loadAppStateWidget
    stepWidgetFromState Step3State.defaultRenderOptions initial

stepWidgetFromState :: Step3State.FormFillRenderOptions -> AppState -> Widget HTML Unit
stepWidgetFromState renderOptions initial = do
    mailbox <- liftEffect $ Ref.new
        { queue: []
        , waiter: Nothing
        }
    epoch <- liftEffect $ Ref.new 0
    bootstrapped <- interpretCommands mailbox $ bootstrap initial
    runAppLoop (appMessageWidget renderOptions) epoch mailbox bootstrapped

runAppLoop :: (AppState -> Widget HTML Msg) -> LoopEpoch -> Mailbox -> AppState -> Widget HTML Unit
runAppLoop renderApp epoch mailbox rawState = do
    let state = rawState
    loopId <- liftEffect $ Ref.modify (_ + 1) epoch
    msg <- nextMsgWidget renderApp epoch loopId mailbox state
    result <- wrapCritical $ update state msg
    nextState <- interpretCommands mailbox result
    runAppLoop renderApp epoch mailbox nextState

nextMsgWidget :: (AppState -> Widget HTML Msg) -> LoopEpoch -> Int -> Mailbox -> AppState -> Widget HTML Msg
nextMsgWidget renderApp epoch loopId mailbox state = do
    queued <- liftEffect $ dequeueIfCurrent epoch loopId mailbox
    case queued of
        Just msg ->
            pure msg

        Nothing -> do
            awaitCurrentLoop case state.step3 of
                Step3Ready ready ->
                    baseSources <|> awaitStep3DocumentRequest ready
                _ ->
                    baseSources
    where
    baseSources =
        renderApp state
            <|> awaitBrowserLocationChange state.pushStateInterface
            <|> awaitMailboxMessage epoch loopId mailbox

    awaitStep3DocumentRequest ready =
        let
            currentDocJson = JSON.stringify $ JSON.encodeJson ready.editor.fillState.doc
        in
        Step3DocumentRequestHandled <$ affAction (Step3DocumentRequest.awaitRequest currentDocJson)

    awaitCurrentLoop widget = do
        msg <- widget
        current <- liftEffect $ Ref.read epoch
        if current == loopId
            then pure msg
            else awaitCurrentLoop widget

appMessageWidget :: Step3State.FormFillRenderOptions -> AppState -> Widget HTML Msg
appMessageWidget renderOptions state = case state.step3, state.step of
    _, Step1 -> case state.step1 of
        Step1Ready ready ->
            Step1Msg <$> Step1.step1MessageWidget (
                if OpStore.isScopeActive state.operations Step1GlobalScope
                    then Step1.startLoading ready
                    else Step1.finishLoading ready
            )
        Step1Loading _ ->
            loadingWidget
        Step1NotLoaded ->
            loadingWidget

    _, Step2A -> case state.step2A of
        Step2AReady _ | OpStore.isScopeActive state.operations Step2ExportScope ->
            loadingWidgetWQ state.workQueueState state.workQueueUi state.workQueueCache
        Step2AReady ready ->
            Step2AAction <$> StudentSelect.studentSelectStepWidget state.workQueueState state.workQueueUi state.workQueueCache ready
        Step2ALoading _ ->
            loadingWidgetWQ state.workQueueState state.workQueueUi state.workQueueCache
        Step2ALoadFailed err ->
            handleCritical err
        Step2ANotLoaded ->
            loadingWidgetWQ state.workQueueState state.workQueueUi state.workQueueCache

    _, Step2B -> case state.step2B of
        Step2BReady _ | OpStore.isScopeActive state.operations Step2ExportScope ->
            loadingWidgetWQ state.workQueueState state.workQueueUi state.workQueueCache
        Step2BReady ready ->
            Step2BAction <$> FormPicker.formPickerStepWidget (AppRoute.routePreservedQuery state.route) ready
        Step2BLoading _ ->
            loadingWidgetWQ state.workQueueState state.workQueueUi state.workQueueCache
        Step2BLoadFailed err ->
            handleCritical err
        Step2BNotLoaded ->
            loadingWidgetWQ state.workQueueState state.workQueueUi state.workQueueCache

    Step3Ready ready, Step3 ->
        Step3Msg <<< Step3Update.ViewEvent <$> renderStep3 renderOptions (AppRoute.routePreservedQuery state.route) ready

    Step3Ready ready, Step3Doc _ ->
        Step3Msg <<< Step3Update.ViewEvent <$> renderStep3 renderOptions (AppRoute.routePreservedQuery state.route) ready

    Step3LoadingFormTypes _, Step3 ->
        loadingWidget

    Step3LoadingFormTypes _, Step3Doc _ ->
        loadingWidget

    Step3LoadingInitialState _, Step3 ->
        loadingWidget

    Step3LoadingInitialState _, Step3Doc _ ->
        loadingWidget

    _, Preview n ->
        Step3DocumentRequestHandled <$ previewWidget n

    _, TestForm ->
        renderTestForm

    _, Step4 ->
        D.div' [ D.text "" ]

    _, _ ->
        loadingWidget

awaitBrowserLocationChange :: PushStateInterface -> Widget HTML Msg
awaitBrowserLocationChange pushStateInterface =
    BrowserLocationChanged <$> affAction (awaitLocationChange pushStateInterface)

awaitLocationChange :: PushStateInterface -> Aff LocationState
awaitLocationChange _ = makeAff \reply -> do
    unlisten <- listenBrowserNavigation \locationState ->
        reply $ Right locationState
    pure $ effectCanceler unlisten

--| Event-driven queue processing.
-- Wait for an internal app message, then hand it back to the main loop.
-- Usually this consists of async returns, like route changes or preview.
awaitMailboxMessage :: LoopEpoch -> Int -> Mailbox -> Widget HTML Msg
awaitMailboxMessage epoch loopId mailbox =
    affAction $ makeAff \reply -> do
        current <- Ref.read epoch

        -- Stops a stale loop installing a waiter
        -- Checks the global loop id, incremented by the app's main loop,
        -- against the loop id passed to `awaitMailboxMessage`
        if current /= loopId then
            pure mempty

        else do
            msgMaybe <- dequeue mailbox

            case msgMaybe of
                -- If there was a message in the queue, return it to the
                -- main app loop.
                Just msg -> do
                    reply $ Right msg
                    pure mempty
                
                -- If there was no message in the queue...
                Nothing -> do
                    -- Create a callback for `enqueue` to call,
                    let wake msg = reply $ Right msg
                    -- Put the callback in the mailbox,
                    Ref.modify_ (_ { waiter = Just { loopId, wake } }) mailbox

                    -- Return a cleanup function to be run on Aff cancellation.
                    pure $ effectCanceler do
                        clearWaiter loopId mailbox

-- Clear a waiter in the current application loop, while preventing
-- an old canceler from clearing a waiter in a later loop iteration.
clearWaiter :: Int -> Mailbox -> Effect Unit
clearWaiter loopId mailbox =
    Ref.modify_ clear mailbox
    where
    clear state = case state.waiter of
        Just waiter | waiter.loopId == loopId ->
            state { waiter = Nothing }
        _ ->
            state

interpretCommands :: Mailbox -> UpdateResult -> Widget HTML AppState
interpretCommands mailbox result = do
    liftEffect $ traverse_ (runCommand mailbox result.state) result.commands
    pure result.state

-- App.Update returns commands instead of running effects itself. This is the
-- single place that turns those commands into effects.
--
-- Commands that have an OperationId are still matched here. Their longer async
-- bodies live in App.Operation.Runner, so this case expression remains the
-- complete list of commands the runtime can execute.
runCommand :: Mailbox -> AppState -> Command -> Effect Unit
runCommand mailbox state = case _ of
    SyncRoute historyMode -> do
        locationState <- readLocation state.pushStateInterface
        let route = AppRoute.routeForStepAndWorkQueueState locationState state.step state.workQueueState
        Console.log $ "APP_SYNC_ROUTE " <> AppRoute.routeUrl route
        case historyMode of
            PushRouteHistory -> pushRoute state.pushStateInterface route
            ReplaceRouteHistory -> replaceRoute state.pushStateInterface route

    HydrateLocation routeVersion locationState ->
        launchAff_ do
            hydrated <- deriveAppState state locationState
            liftEffect $ enqueue mailbox [ HydratedLocation routeVersion locationState hydrated ]

    RunStep1InitialLoad opId initial ->
        OperationRunner.runStep1InitialLoad (enqueue mailbox) opId initial

    DebounceStep1Search version ->
        launchAff_ do
            _ <- delay step1SearchDebounceDelay
            liftEffect $ enqueue mailbox [ Step1SearchDebounced version ]

    RunStep1Search opId ready ->
        OperationRunner.runStep1Search (enqueue mailbox) opId ready

    RunStep1PageChange opId ready idx ->
        OperationRunner.runStep1PageChange (enqueue mailbox) opId ready idx

    RunStep1CurrentPageReload opId ready ->
        OperationRunner.runStep1CurrentPageReload (enqueue mailbox) opId ready

    RunStep1SaveFormOrder opId ready ->
        OperationRunner.runStep1SaveFormOrder (enqueue mailbox) opId ready

    RunStep1ToggleLocks opId ready ->
        OperationRunner.runStep1ToggleLocks (enqueue mailbox) opId ready

    RunStep1CopyDocuments opId ready requests ->
        OperationRunner.runStep1CopyDocuments (enqueue mailbox) opId ready requests

    RunStep1DeleteDocuments opId ready ->
        OperationRunner.runStep1DeleteDocuments (enqueue mailbox) opId ready

    RunStep1ExportDocuments opId ready etype ->
        OperationRunner.runStep1ExportDocuments (enqueue mailbox) opId ready etype

    RunStep1RequestSignatures opId requests ->
        OperationRunner.runStep1RequestSignatures (enqueue mailbox) opId requests

    RunStep1UpdateForms opId ready selection ->
        OperationRunner.runStep1UpdateForms (enqueue mailbox) opId ready selection

    RunStep2AInitialLoad opId workQueueState workQueueCache ->
        OperationRunner.runStep2AInitialLoad (enqueue mailbox) opId workQueueState workQueueCache

    RunStep2BLoad opId workQueueState workQueueUi workQueueCache ->
        OperationRunner.runStep2BLoad (enqueue mailbox) opId workQueueState workQueueUi workQueueCache

    RunStep2ExportAll opId workQueueState workQueueCache ->
        OperationRunner.runStep2ExportAll (enqueue mailbox) opId workQueueState workQueueCache

    RunStep3FormTypesLoad opId init ->
        OperationRunner.runStep3FormTypesLoad (enqueue mailbox) state opId init

    RunStep3InitialLoad opId kind init ->
        OperationRunner.runStep3InitialLoad (enqueue mailbox) state opId kind init

    SyncStep3Route historyMode -> case state.step3 of
        Step3Ready ready -> do
            locationState <- readLocation state.pushStateInterface
            let route = step3RouteForState locationState ready
            let currentUrl = locationState.pathname <> locationState.search <> locationState.hash
            let nextUrl = AppRoute.routeUrl route
            when (nextUrl /= currentUrl) do
                case historyMode of
                    PushStep3History -> pushRoute state.pushStateInterface route
                    ReplaceStep3History -> replaceRoute state.pushStateInterface route
            case ready.editor.sectionIntent of
                AppRoute.SectionSet section -> scrollSectionIntoView section
                _ -> pure unit
        _ ->
            pure unit

    ScrollStep3Section section ->
        scrollSectionIntoView section

    SaveStep3 opId kind generation ->
        OperationRunner.runSaveStep3 (enqueue mailbox) state opId kind generation

    LoadStep3 opId kind target ->
        OperationRunner.runLoadStep3 (enqueue mailbox) state opId kind target

    AutofillStep3 opId kind args ->
        OperationRunner.runAutofillStep3 (enqueue mailbox) opId kind args

    ToggleStep3Lock opId kind ->
        OperationRunner.runToggleStep3Lock (enqueue mailbox) state opId kind

    RunStep3DocumentMutation opId kind request ->
        OperationRunner.runStep3DocumentMutation (enqueue mailbox) opId kind request

    ConfirmStep3Cancel -> do
        confirmed <- manualSavePrompt
        enqueue mailbox [ Step3Msg $ Step3Update.ConfirmCancelResult confirmed ]

    AllowPageUnload ->
        allowPageUnload

    BlockPageUnload ->
        blockPageUnload

    OpenExportUrl url -> do
        win <- JS.window
        void $ Window.open url "_blank" "" win

    OpenExternalUrl url -> do
        win <- JS.window
        void $ Window.open url "_blank" "" win

    ReportError err ->
        void $ reportError err

-- | Add a message to the queue.
-- Note: does not decide if the waiter is within the current loop.
-- Before raising the above as a bug, prove a stale waiter may remain in the
-- mailbox after its branch (in `nextMsgWidget`) has lost the `<|>` race.
enqueue :: Mailbox -> Array Msg -> Effect Unit
enqueue mailbox msgs = do
    state <- Ref.read mailbox

    case state.waiter, Arr.uncons (state.queue <> msgs) of
        Just { wake }, Just { head, tail } -> do
            Ref.write
                { queue: tail
                , waiter: Nothing
                }
                mailbox
            
            wake head
        
        _, _ ->
            Ref.write
                state { queue = state.queue <> msgs }
                mailbox

--| Removes first item from the queue and returns it.
-- Note: does not decide if the waiter is within the current loop.
-- Before raising the above as a bug, prove a stale waiter may remain in the
-- mailbox after its branch (in `nextMsgWidget`) has lost the `<|>` race.
dequeue :: Mailbox -> Effect (Maybe Msg)
dequeue mailbox = do
    Ref.modify' step mailbox
  where
    step state = case Arr.uncons state.queue of
        Nothing ->
            { state: state
            , value: Nothing
            }

        Just { head, tail } ->
            { state: state { queue = tail }
            , value: Just head
            }

dequeueIfCurrent :: LoopEpoch -> Int -> Mailbox -> Effect (Maybe Msg)
dequeueIfCurrent epoch loopId mailbox = do
    current <- Ref.read epoch
    if current == loopId
        then dequeue mailbox
        else pure Nothing

step3RouteForState :: LocationState -> Step3State.Step3State -> AppRoute.Route
step3RouteForState locationState ready =
    AppRoute.RStep3
        { preservedQuery: AppRoute.preserveQuery locationState.search
        , activeDoc: Just ready.editor.fillState.doc.id
        , docs: step3DocIds
        , section: ready.editor.sectionIntent
        }
    where
    step3DocIds =
        Arr.nub $ (map _.id ready.editor.workQueueState.editDocs) <> (map _.id $ Arr.fromFoldable $ listDocs ready.editor.workQueueState.documents)
